import { createCustomEqual } from 'fast-equals'
import React from 'react'
import { isLatLngLiteral } from '@googlemaps/typescript-guards'

export interface IOwnProps extends google.maps.MapOptions {
  style: { [key: string]: string }
  onMapReady?: (map: google.maps.Map) => void
}
export interface IStateProps { }
export interface IDispatchProps { }

export type IProps = IOwnProps & IStateProps & IDispatchProps

const Map: React.FC<IProps> = ({
  children,
  style,
  onMapReady,
  ...options
}) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const [map, setMap] = React.useState<google.maps.Map>()

  React.useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {})
      setMap(newMap)
      // Call onMapReady callback when map is created
      if (onMapReady) {
        onMapReady(newMap)
      }
    }
  }, [ref, map, onMapReady])

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options)
    }
  }, [map, options])

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map })
        }
      })}
    </>
  )
}

const useDeepCompareEffectForMaps = (
  callback: React.EffectCallback,
  dependencies: any[]
) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize))
}

const useDeepCompareMemoize = (value: any) => {
  const ref = React.useRef()

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value
  }

  return ref.current
}

const deepCompareEqualsForMaps = createCustomEqual(
  (deepEqual) => (a: any, b: any) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b))
    }

    // use fast-equals for other objects
    return deepEqual(a, b)
  }
)

export default Map
